import { Injectable, Provider } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, delay, of } from 'rxjs';

import { MOCK_FORM_FIELDS } from '@demo/data/demo-form-builder.const';
import { KeyValueType } from '@models/common';
import { FieldModel, ScreenFieldModel } from '@models/module/fields/main';
import { ScreenModel, ScreenTypeEnum } from '@module-definition/screen/common';
import {
  FieldDataSource,
  ModuleConfigDictionariesService,
} from '@module-instance/modules/dialogs/module-config/common';
import {
  DeletedTableColumn,
  ExpressionField,
  FieldDefinitionsService,
  ListElementModel,
  ScreenFieldsService,
  TableColumnModel,
} from '@module-instance/modules/dialogs/module-config/screen-designer/common';

const DEMO_DELAY = 300;
const DEMO_SCREEN_FIELDS = 6;

const later = <T>(value: T): Observable<T> => of(value).pipe(delay(DEMO_DELAY));

const screenFields = MOCK_FORM_FIELDS.slice(0, DEMO_SCREEN_FIELDS) as unknown as ScreenFieldModel[];
const moduleFields = MOCK_FORM_FIELDS.map(field => ({
  ...field,
  inUse: screenFields.some(entry => entry.id === field.id),
  canRemove: true,
})) as unknown as FieldModel[];

export const DEMO_DESIGNER_SCREEN: ScreenModel = {
  id: 349,
  name: 'Demo start screen',
  type: ScreenTypeEnum.Entity,
  isStartScreen: true,
  inUse: false,
  anyFieldInUse: false,
  roleIds: [],
};

@Injectable()
class DemoScreenFieldsService {
  search(): Observable<ScreenFieldModel[]> {
    return later(screenFields);
  }

  syncToScreen(): Observable<void> {
    return later(undefined);
  }

  getFieldDetails(_moduleId: number, id: number): Observable<FieldModel> {
    return later(moduleFields.find(field => field.id === id));
  }

  updateFieldDetails(): Observable<void> {
    return later(undefined);
  }

  getFieldScreens(): Observable<KeyValueType<number, string>[]> {
    return later([{ key: DEMO_DESIGNER_SCREEN.id, value: DEMO_DESIGNER_SCREEN.name }]);
  }

  getParentDefaultValues(): Observable<number[]> {
    return later([]);
  }

  hasContractFields(): Observable<boolean> {
    return later(false);
  }
}

@Injectable()
class DemoFieldDefinitionsService {
  search(): Observable<FieldModel[]> {
    return later(moduleFields);
  }

  get(_moduleId: number, id: number): Observable<FieldModel> {
    return later(moduleFields.find(field => field.id === id));
  }

  add(): Observable<number> {
    return later(Date.now());
  }

  edit(): Observable<void> {
    return later(undefined);
  }

  delete(): Observable<void> {
    return later(undefined);
  }

  getTableColumns(): Observable<TableColumnModel[]> {
    return later([]);
  }

  getDeletedTableColumns(): Observable<DeletedTableColumn[]> {
    return later([]);
  }

  syncTableColumns(): Observable<void> {
    return later(undefined);
  }

  addTableColumn(): Observable<number> {
    return later(Date.now());
  }

  editTableColumn(): Observable<void> {
    return later(undefined);
  }

  deleteTableColumn(): Observable<void> {
    return later(undefined);
  }

  getExpressionFields(): Observable<ExpressionField[]> {
    return later([]);
  }

  getListElements(): Observable<ListElementModel[]> {
    return later([]);
  }
}

@Injectable()
class DemoModuleConfigDictionariesService {
  invalidate(): void {}

  getModuleEntities(): Observable<KeyValueType<number, string>[]> {
    return later([{ key: 1, value: 'Demo entity' }]);
  }

  getDataSources(): Observable<FieldDataSource[]> {
    return later([]);
  }

  getAggregateFunctions(): Observable<KeyValueType<number, string>[]> {
    return later([
      { key: 1, value: 'SUM' },
      { key: 2, value: 'AVG' },
    ]);
  }

  getModuleFields(): Observable<KeyValueType<number, string>[]> {
    return later([]);
  }
}

export const DEMO_SCREEN_DESIGNER_PROVIDERS: Provider[] = [
  MatDialog,
  { provide: ScreenFieldsService, useClass: DemoScreenFieldsService },
  { provide: FieldDefinitionsService, useClass: DemoFieldDefinitionsService },
  { provide: ModuleConfigDictionariesService, useClass: DemoModuleConfigDictionariesService },
];
